import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Pencil, Trash2, Loader2, Search, X, Upload, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { useAdminAuth } from '@/context/AdminAuthContext';
import {
  adminGetProducts,
  adminCreateProduct,
  adminUpdateProduct,
  adminDeleteProduct,
  adminUploadProductImage,
  adminBulkUploadProductImages,
  getCategories,
} from '@/lib/api';
import { toast } from 'sonner';

const emptyProduct = {
  name: '',
  description: '',
  price: '',
  original_price: '',
  unit: '1 kg',
  category_id: '',
  category_name: '',
  image_url: '',
  in_stock: true,
  stock_quantity: 100
};

export default function AdminProducts() {
  const { isAuthenticated, loading: authLoading } = useAdminAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState(emptyProduct);
  const [saving, setSaving] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkFiles, setBulkFiles] = useState({}); // { productId: File }
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const rowFileInputRefs = useRef({});

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/admin');
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, categoriesRes] = await Promise.all([
          adminGetProducts(),
          getCategories()
        ]);
        setProducts(productsRes.data);
        setCategories(categoriesRes.data);
      } catch (error) {
        console.error('Error fetching products:', error);
        toast.error('Failed to load products');
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated]);

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      ...product,
      price: product.price.toString(),
      original_price: product.original_price?.toString() || '',
      stock_quantity: product.stock_quantity.toString()
    });
    setDialogOpen(true);
  };

  const handleAdd = () => {
    setEditingProduct(null);
    setFormData(emptyProduct);
    setDialogOpen(true);
  };

  const handleCategoryChange = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    setFormData(prev => ({
      ...prev,
      category_id: categoryId,
      category_name: category?.name || ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.price || !formData.category_id || !formData.image_url) {
      toast.error('Please fill all required fields');
      return;
    }

    setSaving(true);
    
    try {
      const productData = {
        ...formData,
        price: parseFloat(formData.price),
        original_price: formData.original_price ? parseFloat(formData.original_price) : null,
        stock_quantity: parseInt(formData.stock_quantity)
      };

      if (editingProduct) {
        await adminUpdateProduct(editingProduct.id, productData);
        setProducts(prev => prev.map(p => 
          p.id === editingProduct.id ? { ...p, ...productData } : p
        ));
        toast.success('Product updated successfully');
      } else {
        const response = await adminCreateProduct(productData);
        setProducts(prev => [...prev, response.data]);
        toast.success('Product created successfully');
      }
      
      setDialogOpen(false);
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error('Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }

    try {
      await adminDeleteProduct(productId);
      setProducts(prev => prev.filter(p => p.id !== productId));
      toast.success('Product deleted');
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Failed to delete product');
    }
  };

  const handleRowImageUpload = async (productId, file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5 MB');
      return;
    }
    setUploadingId(productId);
    try {
      const res = await adminUploadProductImage(productId, file);
      setProducts(prev =>
        prev.map(p => (p.id === productId ? { ...p, image_url: res.data.image_url } : p))
      );
      toast.success('Image uploaded');
    } catch (err) {
      console.error('Upload error:', err);
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingId(null);
    }
  };

  const handleBulkFileChange = (productId, file) => {
    setBulkFiles(prev => {
      const next = { ...prev };
      if (file) next[productId] = file;
      else delete next[productId];
      return next;
    });
  };

  const handleBulkUpload = async () => {
    const entries = Object.entries(bulkFiles);
    if (entries.length === 0) {
      toast.error('Pick at least one image');
      return;
    }
    setBulkUploading(true);
    setBulkResult(null);
    try {
      const res = await adminBulkUploadProductImages(bulkFiles);
      setBulkResult(res.data);
      // Update local product state
      setProducts(prev =>
        prev.map(p => {
          const u = res.data.uploaded.find(x => x.product_id === p.id);
          return u ? { ...p, image_url: u.image_url } : p;
        })
      );
      toast.success(`Uploaded ${res.data.uploaded_count} image(s)`);
      setBulkFiles({});
    } catch (err) {
      console.error('Bulk upload error:', err);
      toast.error(err.response?.data?.detail || 'Bulk upload failed');
    } finally {
      setBulkUploading(false);
    }
  };

  if (authLoading || !isAuthenticated) {
    return null;
  }

  const filteredProducts = products.filter(p => 
    p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.category_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <AdminLayout>
      <div data-testid="admin-products">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <h1 className="text-2xl font-bold">Products</h1>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={() => { setBulkResult(null); setBulkOpen(true); }}
              className="rounded-full"
              data-testid="bulk-upload-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Bulk Upload Images
            </Button>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={handleAdd} className="rounded-full" data-testid="add-product-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Product
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="name">Product Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Fresh Tomatoes"
                    className="mt-1.5"
                    data-testid="product-name-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="price">Price (₹) *</Label>
                    <Input
                      id="price"
                      type="number"
                      value={formData.price}
                      onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                      placeholder="40"
                      className="mt-1.5"
                      data-testid="product-price-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="original_price">Original Price (₹)</Label>
                    <Input
                      id="original_price"
                      type="number"
                      value={formData.original_price}
                      onChange={(e) => setFormData(prev => ({ ...prev, original_price: e.target.value }))}
                      placeholder="50"
                      className="mt-1.5"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="unit">Unit *</Label>
                    <Input
                      id="unit"
                      value={formData.unit}
                      onChange={(e) => setFormData(prev => ({ ...prev, unit: e.target.value }))}
                      placeholder="1 kg"
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="stock">Stock Quantity</Label>
                    <Input
                      id="stock"
                      type="number"
                      value={formData.stock_quantity}
                      onChange={(e) => setFormData(prev => ({ ...prev, stock_quantity: e.target.value }))}
                      placeholder="100"
                      className="mt-1.5"
                    />
                  </div>
                </div>

                <div>
                  <Label>Category *</Label>
                  <Select value={formData.category_id} onValueChange={handleCategoryChange}>
                    <SelectTrigger className="mt-1.5" data-testid="product-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="image_url">Image URL *</Label>
                  <Input
                    id="image_url"
                    value={formData.image_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                    placeholder="https://..."
                    className="mt-1.5"
                    data-testid="product-image-input"
                  />
                </div>

                <div>
                  <Label htmlFor="description">Description</Label>
                  <Input
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Product description"
                    className="mt-1.5"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="in_stock"
                    checked={formData.in_stock}
                    onChange={(e) => setFormData(prev => ({ ...prev, in_stock: e.target.checked }))}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="in_stock">In Stock</Label>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => setDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    className="flex-1"
                    disabled={saving}
                    data-testid="save-product-btn"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    {editingProduct ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            placeholder="Search products..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-11 rounded-full"
            data-testid="search-products"
          />
          {searchQuery && (
            <button 
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2"
            >
              <X className="w-5 h-5 text-muted-foreground" />
            </button>
          )}
        </div>

        {/* Products Table */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">Image</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead className="hidden md:table-cell">Category</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead className="hidden sm:table-cell">Stock</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProducts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      No products found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredProducts.map((product) => (
                    <TableRow key={product.id}>
                      <TableCell>
                        <img
                          src={product.image_url}
                          alt={product.name}
                          className="w-12 h-12 rounded-lg object-cover"
                        />
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium line-clamp-1">{product.name}</p>
                          <p className="text-xs text-muted-foreground">{product.unit}</p>
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        {product.category_name}
                      </TableCell>
                      <TableCell>
                        <p className="font-semibold">₹{product.price}</p>
                        {product.original_price && (
                          <p className="text-xs text-muted-foreground line-through">
                            ₹{product.original_price}
                          </p>
                        )}
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          product.in_stock 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {product.in_stock ? product.stock_quantity : 'Out'}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <input
                            type="file"
                            accept="image/*"
                            ref={(el) => { rowFileInputRefs.current[product.id] = el; }}
                            onChange={(e) => handleRowImageUpload(product.id, e.target.files?.[0])}
                            className="hidden"
                            data-testid={`upload-input-${product.id}`}
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            disabled={uploadingId === product.id}
                            onClick={() => rowFileInputRefs.current[product.id]?.click()}
                            data-testid={`upload-product-${product.id}`}
                            title="Upload product image"
                          >
                            {uploadingId === product.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Upload className="w-4 h-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(product)}
                            data-testid={`edit-product-${product.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleDelete(product.id)}
                            data-testid={`delete-product-${product.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Bulk Upload Dialog */}
        <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="bulk-upload-dialog">
            <DialogHeader>
              <DialogTitle>Bulk Upload Product Images</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              Pick an image (JPG/PNG, &lt; 5 MB) for each product you want to update. Leave others blank.
            </p>
            <div className="max-h-[55vh] overflow-y-auto border rounded-lg divide-y">
              {products.map((p) => {
                const file = bulkFiles[p.id];
                return (
                  <div key={p.id} className="flex items-center gap-3 p-3" data-testid={`bulk-row-${p.id}`}>
                    <img src={p.image_url} alt={p.name} className="w-12 h-12 rounded object-cover bg-muted" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm line-clamp-1">{p.name}</p>
                      <p className="text-xs text-muted-foreground">{p.category_name} · {p.unit}</p>
                      {file && (
                        <p className="text-xs text-primary mt-0.5 flex items-center gap-1">
                          <ImageIcon className="w-3 h-3" />
                          {file.name} ({Math.round(file.size / 1024)} KB)
                        </p>
                      )}
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleBulkFileChange(p.id, e.target.files?.[0])}
                      className="text-xs max-w-[180px]"
                      data-testid={`bulk-file-${p.id}`}
                    />
                  </div>
                );
              })}
            </div>
            {bulkResult && (
              <div className="text-sm border rounded-lg p-3 bg-muted/40" data-testid="bulk-result">
                <p className="font-semibold text-primary mb-1">
                  ✅ {bulkResult.uploaded_count} uploaded
                </p>
                {bulkResult.errors?.length > 0 && (
                  <div className="text-destructive text-xs">
                    {bulkResult.errors.length} failed:
                    <ul className="list-disc ml-5 mt-1">
                      {bulkResult.errors.map((e) => (
                        <li key={`${e.file}-${e.reason}`}>{e.file}: {e.reason}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => { setBulkOpen(false); setBulkFiles({}); setBulkResult(null); }}
              >
                Close
              </Button>
              <Button
                onClick={handleBulkUpload}
                disabled={bulkUploading || Object.keys(bulkFiles).length === 0}
                className="flex-1"
                data-testid="bulk-upload-submit-btn"
              >
                {bulkUploading ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" />Uploading...</>
                ) : (
                  <>Upload {Object.keys(bulkFiles).length || ''} Image{Object.keys(bulkFiles).length === 1 ? '' : 's'}</>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
